<template>
  <BaseDialog
    :open="modelValue"
    :disabled="saving"
    :max-width="'640px'"
    @close="onClose"
  >
    <template #title>
      {{ local.id ? 'Automatik-Angebot bearbeiten' : 'Neues Automatik-Angebot' }}
    </template>

    <form
      class="form"
      @submit.prevent="onSave"
    >
      <div class="row grid-col-2">
        <div class="field col">
          <label for="f-subject">Angebot</label>
          <input
            id="f-subject"
            v-model="local.subject"
            type="text"
            placeholder="z. B. Deutsch 9/10"
          >
        </div>
        <div class="field col">
          <label for="f-location">Ort</label>
          <input
            id="f-location"
            v-model="local.location"
            type="text"
            placeholder="z. B. Schule"
          >
        </div>
      </div>

      <div class="row grid-col-2">
        <div class="field col">
          <label for="f-teacher">Verantwortlich</label>
          <select
            id="f-teacher"
            v-model.number="local.teacher_id"
          >
            <option
              disabled
              value=""
            >
              Bitte wählen…
            </option>
            <option
              v-for="t in teacherOptions"
              :key="t.id"
              :value="t.id"
            >
              {{ t.name }}
            </option>
          </select>
        </div>
        <div class="field col">
          <label
            class="checkbox"
            style="display: flex; align-items: center;"
          >
            <input
              v-model="local.is_mandatory"
              type="checkbox"
            >
            <span>Pflichtangebot</span>
          </label>
        </div>
      </div>

      <div class="row grid-col-4 mt-4">
        <div class="field col">
          <label for="f-cycle">Zyklus</label>
          <select
            id="f-cycle"
            v-model="local.cycle"
          >
            <option
              disabled
              value=""
            >
              Bitte wählen…
            </option>
            <option value="daily">
              täglich
            </option>
            <option value="weekly">
              wöchentlich
            </option>
            <option value="biweekly">
              2‑wöchentlich
            </option>
            <option value="triweekly">
              3‑wöchentlich
            </option>
            <option value="monthly">
              monatlich
            </option>
          </select>
        </div>
        <div class="field col">
          <label for="f-weekday">Wochentag</label>
          <select
            id="f-weekday"
            v-model="local.week_day"
            :disabled="isWeekdayDisabled"
          >
            <option value="">
              -
            </option>
            <option value="1">
              Montag
            </option>
            <option value="2">
              Dienstag
            </option>
            <option value="3">
              Mittwoch
            </option>
            <option value="4">
              Donnerstag
            </option>
            <option value="5">
              Freitag
            </option>
          </select>
        </div>

        <div class="field col">
          <label for="f-start-at">Startzeit</label>
          <flat-pickr
            id="f-start-at"
            v-model="local.start_at"
            :config="fpTimeOpts"
          />
        </div>
        <div class="field col">
          <label for="f-ends-at">Endzeit</label>
          <flat-pickr
            id="f-ends-at"
            v-model="local.ends_at"
            :config="fpTimeOpts"
          />
        </div>
      </div>

      <div class="row grid-col-3">
        <div class="field col mb-0">
          <label for="f-start-date">Startdatum</label>
          <flat-pickr
            id="f-start-date"
            v-model="local.start_date"
            :config="fpDateOpts"
          />
        </div>

        <div class="field col mb-0">
          <label for="f-end-date">Enddatum</label>
          <flat-pickr
            id="f-end-date"
            v-model="local.end_date"
            :config="fpDateOpts"
          />
        </div>

        <div class="field col mb-0">
          <label for="f-status">Status</label>
          <select
            id="f-status"
            v-model="local.status"
          >
            <option value="active">
              aktiv
            </option>
            <option value="paused">
              pausiert
            </option>
          </select>
        </div>
        <div
          v-if="local.id"
          class="info-row--full"
        >
          <span style="font-weight: 500;">ursprünglicher Zeitraum: </span>
          <span>{{ formatDateDmy(local.raw_start_date) }} - {{ formatDateDmy(local.end_date) }}</span>
        </div>
      </div>

      <p
        v-if="error"
        class="notice notice-danger mt-12"
        role="alert"
      >
        {{ error }}
      </p>

      <div
        class="pt-3 mt-12"
        style="display:flex;gap:8px;justify-content:flex-end; border-top: 1px solid #eee; "
      >
        <button
          type="button"
          class="btn"
          :disabled="saving"
          @click="onClose"
        >
          Abbrechen
        </button>
        <button
          type="submit"
          class="btn btn-primary"
          :disabled="saving || !isFormValid"
        >
          <span
            v-if="saving"
            class="spinner"
            aria-hidden="true"
          /> Speichern
        </button>
      </div>
    </form>
  </BaseDialog>
</template>

<script>
import BaseDialog from '@/components/BaseDialog.vue';
import autoOffersApi from '@/services/api/autoOffers';
import api from '@/services/api';
import FlatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import {German} from 'flatpickr/dist/l10n/de.js';

export default {
  name: 'AutoOfferRuleEditDialog',
  components: {BaseDialog, 'flat-pickr': FlatPickr},
  props: {
    modelValue: {type: Boolean, default: false},
    rule: {type: Object, default: null}
  },
  emits: ['update:modelValue', 'saved', 'toast'],
  data() {
    return {
      local: this.cloneRule(this.rule),
      saving: false,
      error: null,
      deleting: false,
      deleteOpen: false,
      deleteError: null,
      fpDateOpts: {
        altInput: true,
        altFormat: 'd.m.Y',
        dateFormat: 'Y-m-d',
        locale: German,
        disableMobile: true,
        minDate: 'today'
      },
      fpTimeOpts: {
        enableTime: true,
        noCalendar: true,
        dateFormat: 'H:i',
        time_24hr: true,
        locale: German,
        allowInput: true,
        disableMobile: true
      },
      teacherOptions: []
    };
  },
  computed: {
    isWeekdayDisabled() {
      return String(this.local?.cycle || '') === 'daily';
    },
    isFormValid() {
      return this.getValidationError() === null;
    }
  },
  watch: {
    modelValue(val) {
      if (val) {
        this.local = this.cloneRule(this.rule);
        this.error = null;
        if (!this.teacherOptions.length) {
          this.loadTeachers();
        }
      }
    },
    rule: {
      handler(n) {
        this.local = this.cloneRule(n);
      },
      deep: true
    },
    'local.cycle': {
      handler(newVal) {
        if (String(newVal || '') === 'daily') {
          this.local.week_day = '';
        }
      }
    },
    'local.start_at': {
      handler(newVal) {
        const end = this.addMinutesToTimeString(newVal, 90);
        if (end) {
          this.local.ends_at = end;
        }
      }
    }
  },
  methods: {
    cloneRule(r) {
      const base = r || {};

      const startAt = (base.start_at || '').trim() || '08:30';
      const endsAtRaw = (base.ends_at || '').trim();

      const today = this.todayIsoDate();
      const normalizedStartDate = this.normalizeStartDateNotBeforeToday(base.start_date || '', today);
      const resolvedCycle = String(base.rule ?? '').trim();

      const isCreate = !base.id;
      const endDateFromBackend = String(base.end_date || '').trim();
      const resolvedEndDate = endDateFromBackend || (isCreate ? this.nextJuly31IsoDate(today) : '');

      return {
        id: base.id || null,
        subject: base.subject || '',
        location: base.location || '',
        teacher_id: base.teacher_id || '',
        is_mandatory: Boolean(base.is_mandatory ?? base.mandatory ?? false),
        cycle: resolvedCycle,
        week_day: (resolvedCycle === 'daily' ? '' : (base.week_day ?? '')),
        raw_start_date: base.start_date,
        start_date: normalizedStartDate,
        end_date: resolvedEndDate,
        start_at: startAt,
        ends_at: endsAtRaw || (this.addMinutesToTimeString(startAt, 90) || ''),
        status: base.status || 'active'
      };
    },
    getValidationError() {
      const subject = String(this.local?.subject || '').trim();
      const location = String(this.local?.location || '').trim();
      const teacherId = Number(this.local?.teacher_id || 0);
      const cycle = String(this.local?.cycle || '').trim();
      const weekDay = String(this.local?.week_day || '').trim();
      const startAt = String(this.local?.start_at || '').trim();
      const endsAt = String(this.local?.ends_at || '').trim();
      const startDate = String(this.local?.start_date || '').trim();
      const endDate = String(this.local?.end_date || '').trim();
      const status = String(this.local?.status || '').trim();

      if (!subject) return 'Bitte alle Pflichtfelder ausfüllen (Angebot).';
      if (!location) return 'Bitte alle Pflichtfelder ausfüllen (Ort).';
      if (!Number.isFinite(teacherId) || teacherId <= 0) return 'Bitte alle Pflichtfelder ausfüllen (Verantwortlich).';
      if (!cycle) return 'Bitte alle Pflichtfelder ausfüllen (Zyklus).';

      if (cycle !== 'daily' && !weekDay) return 'Bitte alle Pflichtfelder ausfüllen (Wochentag).';

      if (!startAt) return 'Bitte alle Pflichtfelder ausfüllen (Startzeit).';
      if (!endsAt) return 'Bitte alle Pflichtfelder ausfüllen (Endzeit).';
      if (!startDate) return 'Bitte alle Pflichtfelder ausfüllen (Startdatum).';
      if (!endDate) return 'Bitte alle Pflichtfelder ausfüllen (Enddatum).';
      if (!status) return 'Bitte alle Pflichtfelder ausfüllen (Status).';

      return null;
    },
    formatDateDmy(val) {
      const s = String(val || '').trim();
      if (!s) return '';
      const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) return `${m[3]}.${m[2]}.${m[1]}`;
      return s;
    },
    todayIsoDate() {
      const d = new Date();
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    },
    nextJuly31IsoDate(todayIso) {
      const today = this.parseYmdToDate(todayIso);
      const y = today ? today.getFullYear() : new Date().getFullYear();

      const july31ThisYear = new Date(y, 6, 31, 0, 0, 0, 0); // Monat: 0=Jan ... 6=Jul
      const target = (today && today > july31ThisYear) ? new Date(y + 1, 6, 31, 0, 0, 0, 0) : july31ThisYear;

      const yyyy = target.getFullYear();
      const mm = String(target.getMonth() + 1).padStart(2, '0');
      const dd = String(target.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    },
    parseYmdToDate(val) {
      const m = String(val || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!m) return null;
      const y = Number(m[1]);
      const mo = Number(m[2]) - 1;
      const d = Number(m[3]);
      const dt = new Date(y, mo, d, 0, 0, 0, 0);
      return Number.isNaN(dt.getTime()) ? null : dt;
    },
    normalizeStartDateNotBeforeToday(startDateStr, todayIso) {
      const todayDate = this.parseYmdToDate(todayIso);
      const startDate = this.parseYmdToDate(startDateStr);

      if (!todayDate) return String(startDateStr || '').trim() || todayIso;
      if (!startDate) return todayIso;

      return startDate < todayDate ? todayIso : startDateStr;
    },
    parseHHMM(val) {
      const m = String(val || '').trim().match(/^([01]\d|2[0-3]):([0-5]\d)$/);
      if (!m) return null;
      return {hh: Number(m[1]), mm: Number(m[2])};
    },
    addMinutesToTimeString(timeStr, minutesToAdd) {
      const t = this.parseHHMM(timeStr);
      if (!t) return null;

      const total = (t.hh * 60 + t.mm + Number(minutesToAdd || 0)) % (24 * 60);
      const hh = String(Math.floor(total / 60)).padStart(2, '0');
      const mm = String(total % 60).padStart(2, '0');
      return `${hh}:${mm}`;
    },
    async loadTeachers() {
      try {
        const res = await api.get('/users/assignable', {params: {roles: '2'}});
        const raw = res?.data?.data ?? res?.data ?? [];
        const list = Array.isArray(raw) ? raw : [];
        this.teacherOptions = list
            .map(u => ({
              id: Number(u.id ?? 0),
              name: String(u.displayName ?? u.name ?? '').trim()
            }))
            .filter(u => Number.isFinite(u.id) && u.id > 0 && u.name);
      } catch (e) {
        this.teacherOptions = [];
      }
    },
    onClose() {
      this.$emit('update:modelValue', false);
    },
    emitToast(severity, summary) {
      this.$emit('toast', {
        severity,
        summary: String(summary || '').trim() || 'Aktion ausgeführt.'
      });
    },
    async onSave() {
      const validationError = this.getValidationError();
      if (validationError) {
        this.error = validationError;
        this.emitToast('warn', validationError);
        return;
      }

      this.saving = true;
      this.error = null;
      try {
        const payload = {...this.local};
        if (payload.id) {
          await autoOffersApi.update(payload.id, payload);
        } else {
          await autoOffersApi.create(payload);
        }

        this.emitToast('success', 'Gespeichert.');
        this.$emit('saved');
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || 'Speichern fehlgeschlagen.';
        this.error = String(msg);
        this.emitToast('error', msg);
      } finally {
        this.saving = false;
      }
    },
    async onDeleteConfirm() {
      this.deleting = true;
      this.deleteError = null;
      try {
        await autoOffersApi.remove(this.local.id);
        this.deleteOpen = false;

        this.emitToast('success', 'Gelöscht.');
        this.$emit('saved');
        this.$emit('update:modelValue', false);
      } catch (e) {
        this.deleteError = 'Löschen fehlgeschlagen.';
        this.emitToast('error', this.deleteError);
      } finally {
        this.deleting = false;
      }
    }
  }
};
</script>

<style scoped>
.help {
  font-size: 12px;
  line-height: 16px;
  color: var(--muted);
}

.mt-12 {
  margin-top: 12px;
}

.row {
  display: grid;
  gap: 12px;
}

.row.grid-col-2 {
  grid-template-columns: repeat(2, 1fr);
}

.row.grid-col-3 {
  grid-template-columns: repeat(3, 1fr);
}

.row.grid-col-4 {
  grid-template-columns: repeat(4, 1fr);
}

.col {
  display: grid;
  align-items: end;
  padding: 0;
  margin-bottom: 10px;
}

.field label {
  margin-bottom: 4px;
}

.field label.checkbox input {
  width: 34px;
  height: 20px;
}

.info-row--full {
  grid-column: 1 / -1;
  margin-top: -10px;
  font-size: 12px;
  color: #777;
  padding-left: 3px;
}

.btn {
  padding: .35rem .6rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
}

.btn-primary {
  background: var(--primary);
  color: #fff;
  border-color: var(--primary);
}

:deep(footer) {
  display: none;
}

.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid var(--border);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
